import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  authService,
  type LoginCredentials,
  type AuthResponse,
} from "@shared/services/authService";
import { AUTH_KEYS } from "@shared/constants/queryKeys";

interface UseLoginMutationReturn {
  login: (credentials: LoginCredentials) => void;
  isLoading: boolean;
  error: Error | null;
  isSuccess: boolean;
  reset: () => void;
}

export function useLoginMutation(): UseLoginMutationReturn {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const mutation = useMutation<AuthResponse, Error, LoginCredentials>({
    mutationKey: AUTH_KEYS.login(),
    mutationFn: async (credentials: LoginCredentials) => {
      const response = await authService.login(credentials);
      return response;
    },
    onSuccess: () => {
      // Redirigir al dashboard (todos los usuarios son admin)
      navigate("/admin/dashboard");

      // Invalidar queries relacionadas con autenticación
      queryClient.invalidateQueries({ queryKey: AUTH_KEYS.all });
    },
    onError: () => {
      // Error manejado por React Query
    },
  });

  return {
    login: mutation.mutate,
    isLoading: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
}

interface UseLogoutMutationReturn {
  logout: () => void;
  isLoading: boolean;
  error: Error | null;
  isSuccess: boolean;
}

export function useLogoutMutation(): UseLogoutMutationReturn {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const mutation = useMutation<void, Error, void>({
    mutationKey: AUTH_KEYS.logout(),
    mutationFn: async () => {
      await authService.logout();
    },
    onSuccess: () => {
      // Limpiar todas las queries del cache
      queryClient.clear();

      // Redirigir al login
      navigate("/login");
    },
    onError: () => {
      // Error manejado por React Query
    },
  });

  return {
    logout: mutation.mutate,
    isLoading: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
  };
}
